import * as THREE from 'three';
import { gradeLand } from './grade.js';

export function seededRandom(seed: number) {
  return () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
}

/** Bounding radius, in metres, under which a scattered detail casts no shadow. */
const MINOR_RADIUS = .1;
let instancedDepth: THREE.MeshDepthMaterial | undefined;
/** Whether three draws this material's shadow with no texture or cut-out, so one shared depth material stays on one program. */
function plainDepth(material: THREE.Material) {
  const standard = material as THREE.MeshStandardMaterial;
  return !(material.alphaTest > 0 || material.alphaToCoverage || standard.map || standard.alphaMap || (standard.displacementMap && standard.displacementScale) || material.clippingPlanes?.length);
}

/** Tile static details so Three can cull whole batches outside the camera. */
export class SceneryBatches {
  private batches = new Map<string, { geometry: THREE.BufferGeometry; material: THREE.Material; matrices: THREE.Matrix4[]; colors: THREE.Color[]; shadow: boolean }>();
  private dummy = new THREE.Object3D();
  constructor(private scene: THREE.Scene, private groundHeight: (x: number, z: number) => number = () => 0) {}
  height(x: number, z: number) { return this.groundHeight(x, z); }
  add(key: string, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number,
    sx: number, sy: number, sz: number, rotation = 0, color = 0xffffff, shadow = true) {
    const tile = `${key}:${Math.floor(x / 80)}:${Math.floor(z / 80)}`;
    let batch = this.batches.get(tile);
    if (!batch) { batch = { geometry, material, matrices: [], colors: [], shadow }; this.batches.set(tile, batch); }
    this.dummy.position.set(x, y + this.groundHeight(x, z), z); this.dummy.scale.set(sx, sy, sz); this.dummy.rotation.set(0, rotation, 0); this.dummy.updateMatrix();
    batch.matrices.push(this.dummy.matrix.clone()); batch.colors.push(gradeLand(new THREE.Color(color)));
  }
  flush() {
    for (const batch of this.batches.values()) {
      const mesh = new THREE.InstancedMesh(batch.geometry, batch.material, batch.matrices.length);
      batch.matrices.forEach((matrix, index) => { mesh.setMatrixAt(index, matrix); mesh.setColorAt(index, batch.colors[index]); });
      // Pebbles, petals and twigs smaller than this are lost in the shadow map, as minor figure parts are (`figure-shadows.ts`).
      if (!batch.geometry.boundingSphere) batch.geometry.computeBoundingSphere();
      const largest = Math.max(...batch.matrices.map(matrix => matrix.getMaxScaleOnAxis()));
      if (batch.geometry.boundingSphere!.radius * largest < MINOR_RADIUS) batch.shadow = false;
      mesh.castShadow = batch.shadow; mesh.receiveShadow = true; mesh.computeBoundingSphere(); this.scene.add(mesh);
      // Three's one depth material otherwise changes program whenever consecutive shadow draws switch between instanced and single meshes.
      if (batch.shadow && plainDepth(batch.material)) mesh.customDepthMaterial = instancedDepth ??= new THREE.MeshDepthMaterial();
    }
    this.batches.clear();
  }
}

let contact: { geometry: THREE.BufferGeometry; material: THREE.Material } | undefined;
/**
 * A soft dark patch on the ground around a rock's foot: cheap ambient occlusion, so stones sit in the grass instead of resting on it.
 * `radius` is the rock's footprint in metres. Rocks too small to read (or that already cast a real shadow at pebble scale) can skip it.
 */
export function contactShadow(batches: SceneryBatches, x: number, z: number, radius: number, rotation = 0) {
  if (!contact) {
    const size = 64, data = new Uint8Array(size * size * 4);
    for (let i = 0; i < size * size; i++) {
      const d = Math.hypot((i % size + .5) / size - .5, (Math.floor(i / size) + .5) / size - .5) * 2, t = Math.min(1, Math.max(0, 1 - d)), alpha = t * t * (3 - 2 * t);
      data.set([255, 255, 255, Math.round(alpha * 242)], i * 4);
    }
    const map = new THREE.DataTexture(data, size, size, THREE.RGBAFormat); map.colorSpace = THREE.SRGBColorSpace; map.magFilter = map.minFilter = THREE.LinearFilter; map.needsUpdate = true;
    contact = {
      geometry: new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2),
      material: new THREE.MeshBasicMaterial({ map, transparent: true, opacity: .4, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }),
    };
  }
  batches.add('contact-shadow', contact.geometry, contact.material, x, .035, z, radius, 1, radius, rotation, 0x0b1208, false);
}
